/* eslint-disable max-classes-per-file */
import { Injectable, inject } from '@angular/core';
import { combineLatest, filter, map, Observable, of, shareReplay, switchMap } from 'rxjs';

import { ICurrentConfigurationsService, ProjectDashboardConfiguration } from './current-configuration.interface';
import { GlobalConfigurationsService } from './global-configuration.service';
import { PermissionsService } from './permissions.service';
import { WorkPackagesService } from './work-package.service';
import { CurrentConfigurationDto } from '@api/backend-connector/models';
import { CurrentConfigurationApi } from '@api/backend-connector/services';

const ProjectDashboardProperty = 'projectDashboard';

// modules enabled by default (must be turned off explicitely)
const InventoryDisabledProperty = 'inventoryDisabled';
const FilesDisabledProperty = 'filesDisabled';
const GlobalConfigurationDisabledProperty = 'globalConfigurationDisabled';

// modules disabled by default (must be turned on explicitely)
const WorkPackageShowTimeEnabledProperty = 'workPackageShowTimeEnabled';
const WorkPackageShowExtraDatesEnabledProperty = 'workPackageShowExtraDatesEnabled';
const WorkPackageShowCreatedAt = 'workPackageShowCreatedAt';
const WorkpackageTemplateEnabled = 'workpackageTemplateEnabled';
const WorkpackageTemplateGroupsEnabled = 'workpackageTemplateGroupsEnabled';
const AssetFilterEnabledProperty = 'assetFilterEnabled'; // this also requires a dynamic field to work
const GuastoFilterEnabledProperty = 'guastoFilterEnabled'; // this also requires a dynamic field to work
export const KnownCustomDynamicFields = {
  guasto: 'guasto',
  asset: 'asset',
  cadenzaScheda: 'cadenzaScheda',
};

export const AuditLogVisibilityEnforcingGrantProperty = 'auditLogVisibilityEnforcingGrant';

@Injectable({
  providedIn: 'root',
})
export class CurrentConfigurationsService implements ICurrentConfigurationsService {
  private readonly currentConfigurationApi = inject(CurrentConfigurationApi);
  private readonly globalConfigurationsService = inject(GlobalConfigurationsService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly workPackagesService = inject(WorkPackagesService);

  private _currentConfiguration$ = combineLatest([
    this.permissionsService.isSuperAdmin(),
    this.globalConfigurationsService.refresh$,
  ]).pipe(
    switchMap(([isSuperAdmin]) => {
      if (isSuperAdmin) {
        return of(undefined);
      }
      return this.currentConfigurationApi.getCurrentConfiguration();
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  getCurrentConfigurationOrEmpty(): Observable<CurrentConfigurationDto | undefined> {
    return this._currentConfiguration$;
  }

  getCurrentConfiguration(): Observable<CurrentConfigurationDto> {
    return this._currentConfiguration$.pipe(filter((c): c is CurrentConfigurationDto => !!c));
  }

  /**
   * Used in project creation. Budget can be enabled in other projects
   * @returns
   */
  budgetModuleEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c?.budgetModuleEnabled));
  }

  inventoryEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[InventoryDisabledProperty]));
  }

  filesEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[FilesDisabledProperty]));
  }

  globalConfigurationEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[GlobalConfigurationDisabledProperty]));
  }

  workpackageTemplateEnabled() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[WorkpackageTemplateEnabled]));
  }

  workpackageTemplateGroupsEnabled() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[WorkpackageTemplateGroupsEnabled]));
  }

  projectDashboardConfiguration(): Observable<ProjectDashboardConfiguration> {
    return this._currentConfiguration$.pipe(map((c) => (c && c.data[ProjectDashboardProperty]) ?? {}));
  }

  showCustomAssetFilter() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[AssetFilterEnabledProperty]));
  }

  showCustomGuastoFilter() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[GuastoFilterEnabledProperty]));
  }

  showWorkPackageTime() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[WorkPackageShowTimeEnabledProperty]));
  }

  showWorkPackageExtraDates() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[WorkPackageShowExtraDatesEnabledProperty]));
  }

  showWorkPackageCreatedAt() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[WorkPackageShowCreatedAt]));
  }

  projectStatuses() {
    return this._currentConfiguration$.pipe(map((c) => (c ? c.projectStatuses : undefined)));
  }

  pushNotificationsPublicKey() {
    return this._currentConfiguration$.pipe(map((c) => (c ? c.pushPublicKey : undefined)));
  }

  /**
   * If present the user must have this distinct grant to see the Acrivity Log info
   * @returns
   */
  auditLogVisibilityEnforcingGrant() {
    return this._currentConfiguration$.pipe(
      map((c) => c?.data?.[AuditLogVisibilityEnforcingGrantProperty] as string | undefined),
    );
  }
}
